import { useCallback, useEffect, useRef, useState } from "react";
const snapshots = new Map();
const BASE = process.env.REACT_APP_BACKEND_URL;

export const useNewsFeed = (params) => {
  const key = JSON.stringify(params);
  const [data, setData] = useState(() => snapshots.get(key) || null);
  const [loading, setLoading] = useState(!snapshots.has(key));
  const [error, setError] = useState("");
  const current = useRef({ key, data });
  const abort = useRef(null);
  const busy = useRef(false);
  const load = useCallback(async (append = false) => {
    if (append && busy.current) return;
    abort.current?.abort();
    const controller = new AbortController(); abort.current = controller;
    busy.current = true; setLoading(true); setError("");
    const previous = current.current.key === key ? current.current.data : null;
    const query = new URLSearchParams();
    Object.entries(JSON.parse(key)).forEach(([k, v]) => { if (v != null && v !== "") query.set(k, v); });
    query.set("page", append ? (previous?.page || 1) + 1 : Number(query.get("page")) || 1);
    query.set("limit", 30);
    if (append && previous?.as_of) query.set("as_of", previous.as_of);
    try {
      const r = await fetch(`${BASE}/api/stories/archive?${query}`, { credentials: "include", signal: controller.signal });
      if (!r.ok) throw new Error(r.status === 404 ? "This news edition is unavailable." : "News could not be loaded. Please retry.");
      const next = await r.json();
      if (controller.signal.aborted) return;
      const combined = append ? [...(previous?.stories || []), ...next.stories] : next.stories;
      const result = { ...next, stories: [...new Map(combined.map(s => [s.id, s])).values()], scrolls: append ? previous?.scrolls || {} : {} };
      current.current = { key, data: result }; snapshots.set(key, result); setData(result);
      if (snapshots.size > 20) snapshots.delete(snapshots.keys().next().value);
    } catch (e) { if (!controller.signal.aborted) setError(e.message); }
    finally { if (!controller.signal.aborted) { busy.current = false; setLoading(false); } }
  }, [key]);
  useEffect(() => {
    abort.current?.abort(); busy.current = false;
    const saved = snapshots.get(key);
    current.current = { key, data: saved || null };
    setData(saved || null); setError(""); setLoading(!saved);
    if (!saved) load();
    return () => abort.current?.abort();
  }, [key, load]);
  const remember = useCallback((scroll, mode = "grid") => {
    const d = current.current.data;
    if (d) { d.scrolls = { ...d.scrolls, [mode]: scroll }; snapshots.set(key, d); }
  }, [key]);
  return { data: current.current.key === key ? data : null, loading, error, loadMore: () => load(true), refresh: () => load(false), remember, key };
};